import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';
import { useConfirm } from '../../components/ConfirmModal';

const STATUS_COLOR = { active: 'success', completed: 'primary', paused: 'warning' };

const AdminSeries = () => {
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [series, setSeries]   = useState([]);
  const [total, setTotal]     = useState(0);
  const [pages, setPages]     = useState(1);
  const [statusCounts, setStatusCounts] = useState({ active: 0, completed: 0, paused: 0 });
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const status = searchParams.get('status') || '';
  const search = searchParams.get('search') || '';
  const page   = parseInt(searchParams.get('page') || '1');
  const [searchInput, setSearchInput] = useState(search);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (status) params.status = status;
    if (search) params.search = search;
    API.get('/admin/series', { params })
      .then(r => {
        setSeries(r.data.series);
        setTotal(r.data.total);
        setPages(r.data.pages);
        setStatusCounts(r.data.statusCounts || {});
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [status, search, page]);

  useEffect(() => { load(); }, [load]);

  const set = (key, val) => {
    const p = Object.fromEntries(searchParams);
    if (val) p[key] = val; else delete p[key];
    if (key !== 'page') p.page = '1';
    setSearchParams(p);
  };

  const handleDelete = async (id, title) => {
    const ok = await confirm({ title: 'Delete series?', message: `"${title}" and all its chapters will be permanently deleted.`, confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    setDeleting(id);
    try {
      await API.delete(`/admin/series/${id}`);
      load();
    } catch (e) { console.error(e); }
    finally { setDeleting(null); }
  };

  return (
    <AdminLayout title="Story Series">
      {/* Status tabs */}
      <div className="d-flex gap-2 flex-wrap align-items-center mb-4">
        {[
          { val: '',          label: 'All',       count: total },
          { val: 'active',    label: '▶ Active',  count: statusCounts.active },
          { val: 'completed', label: '✅ Completed', count: statusCounts.completed },
          { val: 'paused',    label: '⏸ Paused',  count: statusCounts.paused },
        ].map(tab => (
          <button key={tab.val} onClick={() => set('status', tab.val)}
            className={`btn btn-sm rounded-pill px-3 ${status === tab.val ? 'btn-dream' : 'btn-outline-secondary'}`}
            style={{ fontSize: '13px' }}>
            {tab.label}
            {tab.count > 0 && (
              <span className="badge rounded-pill ms-1"
                style={{ background: status === tab.val ? 'rgba(255,255,255,0.3)' : 'var(--header-bg)', color: status === tab.val ? '#fff' : 'var(--text-body)', fontSize: '10px' }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}

        <form className="ms-auto d-flex gap-2" onSubmit={e => { e.preventDefault(); set('search', searchInput); }}>
          <input className="form-control form-control-sm" placeholder="Search title…" value={searchInput}
            onChange={e => setSearchInput(e.target.value)} style={{ borderRadius: '8px', width: '200px' }} />
          <button type="submit" className="btn btn-sm btn-outline-secondary rounded-pill">
            <i className="bi bi-search"></i>
          </button>
        </form>

        <span className="text-muted small">{total} series</span>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
        </div>
      ) : (
        <>
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ background: 'var(--page-bg)' }}>
                  <tr>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>SERIES</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>USER</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>CHILD</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>CHAPTERS</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>STATUS</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>LANG</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>CREATED</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {series.length === 0 ? (
                    <tr><td colSpan={8} className="text-center text-muted py-5">No series found</td></tr>
                  ) : series.map(s => (
                    <tr key={s._id} style={{ verticalAlign: 'middle' }}>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', maxWidth: '240px' }}>
                        <div className="d-flex align-items-center gap-2">
                          {s.coverIllustrationUrl ? (
                            <img src={s.coverIllustrationUrl} alt="" className="rounded-2 flex-shrink-0"
                              style={{ width: '36px', height: '36px', objectFit: 'cover' }} />
                          ) : (
                            <div className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0"
                              style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg,#6f42c1,#9b59b6)', fontSize: '18px' }}>
                              📚
                            </div>
                          )}
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px' }}>
                              {s.title}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-soft)' }}>{s.genre}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                        {s.parent && (
                          <Link to={`/admin/users/${s.parent._id}`} style={{ fontSize: '13px', color: '#6f42c1', fontWeight: 600, textDecoration: 'none', display: 'block' }}>
                            {s.parent.name}
                          </Link>
                        )}
                        <div style={{ fontSize: '11px', color: 'var(--text-soft)' }}>{s.parent?.email}</div>
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '13px', color: 'var(--text-main)' }}>
                        {s.child?.avatar} {s.child?.name}, {s.child?.age}y
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '14px', fontWeight: 700, color: '#6f42c1', textAlign: 'center' }}>
                        {s.chapterCount}
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                        <span className={`badge bg-${STATUS_COLOR[s.status]} bg-opacity-10 text-${STATUS_COLOR[s.status]}`} style={{ fontSize: '11px' }}>
                          {s.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '13px', color: 'var(--text-muted2)' }}>
                        {s.language?.toUpperCase()}
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '12px', color: 'var(--text-muted2)' }}>
                        {new Date(s.createdAt).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                        <button disabled={deleting === s._id} onClick={() => handleDelete(s._id, s.title)}
                          className="btn btn-sm btn-outline-danger rounded-pill px-2" style={{ fontSize: '11px' }}>
                          {deleting === s._id
                            ? <span className="spinner-border spinner-border-sm"></span>
                            : <i className="bi bi-trash"></i>}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {pages > 1 && (
            <div className="d-flex justify-content-center gap-2 mt-4">
              <button className="btn btn-sm btn-outline-secondary rounded-pill" disabled={page <= 1}
                onClick={() => set('page', String(page - 1))}>← Prev</button>
              <span className="btn btn-sm disabled">{page} / {pages}</span>
              <button className="btn btn-sm btn-outline-secondary rounded-pill" disabled={page >= pages}
                onClick={() => set('page', String(page + 1))}>Next →</button>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default AdminSeries;
